/**
 * O RENDERIZADOR ANTIGO. Mantido só para comparação, atrás de flag.
 *
 * Ele decide layout por conta própria: 38 `getStyle(id)` com fallback, a regra
 * "cor própria anula opacity" repetida oito vezes, posições calculadas aqui
 * dentro. O worker de e-mail tomava as MESMAS decisões noutro arquivo, e as
 * duas cópias chegaram a 15 divergências ativas.
 *
 * Quem desenha agora é `CertificateCanvas`, sobre a lista que o `resolve`
 * produz. Este arquivo fica por duas semanas para a comparação lado a lado
 * continuar possível — a página `/debug/certificados-comparacao` usa os dois —
 * e some na limpeza da Fase D.
 *
 * NÃO CORRIJA NADA AQUI. Um defeito encontrado neste arquivo é defeito do
 * `resolve`, e é lá que se conserta; mexer aqui só recria a segunda verdade.
 */

import React, { memo, useRef, useCallback, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { getCertificateMetaRows, getCertificateParticipantDisplayName } from "@/lib/certificate-layout";
import type {
  CertificateElement,
  CertificateElementId,
  CertificateElementPosition,
} from "./CertificateElementTypes";
import { getDefaultElements, migrateLegacyPositions } from "./CertificateElementTypes";

// =============================================================================
// A4 Landscape dimensions at 96 DPI (standard web resolution)
// A4 = 297mm x 210mm -> 1123px x 794px at 96 DPI
// These dimensions NEVER change. The preview is always rendered at this exact
// pixel size. For display, a wrapper applies CSS transform:scale() to fit
// the available container. This guarantees WYSIWYG: what you see is exactly
// what gets captured for the PDF, pixel-for-pixel.
// =============================================================================
export const CERTIFICATE_WIDTH = 1123;
export const CERTIFICATE_HEIGHT = 794;

export interface CertificateFields {
  showEventName: boolean;
  showParticipantName: boolean;
  showParticipantLastName: boolean;
  showCPF: boolean;
  maskCPF?: boolean;
  showEventDate: boolean;
  eventDateMode?: "start" | "end" | "range";
  showEventTime?: boolean;
  showEventLocation: boolean;
  showWorkload: boolean;
  showSigners: boolean;
  showQRCode?: boolean;
}

export interface CertificateTextConfig {
  title: string;
  introText: string;
  participationText: string;
  conclusionText: string;
}

export interface CertificateSigner {
  name: string;
  role: string;
  signatureUrl?: string;
}

export interface CertificateSampleData {
  eventName: string;
  participantName: string;
  participantCPF: string;
  eventDate: string;
  eventTime?: string;
  eventLocation: string;
  certificateCode: string;
}

export interface CertificateFontSizes {
  title: "xs" | "sm" | "base" | "lg" | "xl" | "2xl" | "3xl" | "4xl";
  name: "sm" | "base" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";
  event: "xs" | "sm" | "base" | "lg" | "xl" | "2xl" | "3xl";
}

export interface CertificateTextPositions {
  titleTop: number;
  nameTop: number;
  metadataTop: number;
}

export interface CertificatePreviewProps {
  backgroundUrl?: string;
  fields: CertificateFields;
  textConfig: CertificateTextConfig;
  signers: CertificateSigner[];
  workloadHours?: number;
  sampleData: CertificateSampleData;
  textColor?: string;
  textPositions?: { titleTop: number; nameTop: number; metadataTop: number };
  fontSizes?: CertificateFontSizes;
  elements?: CertificateElement[];
  className?: string;
  onPreviewReady?: () => void;
  mode?: "producer" | "production";
  onElementMove?: (id: CertificateElementId, position: CertificateElementPosition) => void;
  selectedElementId?: CertificateElementId | null;
  onSelectElement?: (id: CertificateElementId | null) => void;
}

const DEFAULT_FONT_SIZES: CertificateFontSizes = {
  title: "xl",
  name: "4xl",
  event: "2xl",
};

// Font sizes in PIXELS (absolute, not rem-based).
// This is critical for WYSIWYG — zooming the browser does NOT affect these.
const FONT_SIZE_PX: Record<string, number> = {
  xs: 12,
  sm: 14,
  base: 16,
  lg: 18,
  xl: 20,
  "2xl": 24,
  "3xl": 30,
  "4xl": 36,
  "5xl": 48,
};

function getFontSizePx(size?: string): number {
  return FONT_SIZE_PX[size || "base"] || 16;
}

function getFontWeightCss(weight?: string): string | undefined {
  switch (weight) {
    case "bold": return "700";
    case "semibold": return "600";
    default: return "400";
  }
}

function formatCpf(value: string, mask: boolean): string {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";
  if (mask && digits.length >= 11) {
    return `***.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  }
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  }
  return value;
}

export const CertificatePreviewLegado = memo(function CertificatePreviewLegado({
  backgroundUrl,
  fields,
  textConfig,
  signers,
  workloadHours,
  sampleData,
  textColor = "#1a1a2e",
  textPositions,
  fontSizes = DEFAULT_FONT_SIZES,
  elements: propElements,
  className,
  onPreviewReady,
  mode = "producer",
  onElementMove,
  selectedElementId,
  onSelectElement,
}: CertificatePreviewProps) {
  const { t } = useTranslation("producerCertificates");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [dragState, setDragState] = useState<{
    id: CertificateElementId;
    startX: number;
    startY: number;
    startPos: CertificateElementPosition;
  } | null>(null);

  // Compute responsive scale so the fixed-size preview fits its container
  useEffect(() => {
    const measure = () => {
      if (!wrapperRef.current) return;
      const parent = wrapperRef.current.parentElement;
      if (!parent) return;

      const containerW = parent.clientWidth;
      
      // Se a div wrapper possui a classe h-full, significa que o pai determina a altura disponível
      const hasFixedHeight = wrapperRef.current.classList.contains('h-full') || wrapperRef.current.style.height === '100%';
      const containerH = hasFixedHeight ? parent.clientHeight : Infinity;

      const scaleW = containerW / CERTIFICATE_WIDTH;
      const scaleH = containerH / CERTIFICATE_HEIGHT;
      setScale(Math.min(scaleW, scaleH, 1)); // never upscale beyond 1:1
    };
    measure();
    window.addEventListener("resize", measure);
    
    // observe the parent instead of the wrapper to avoid loops
    const parent = wrapperRef.current?.parentElement;
    const ro = "ResizeObserver" in window ? new ResizeObserver(measure) : null;
    if (ro && parent) ro.observe(parent);

    return () => {
      window.removeEventListener("resize", measure);
      ro?.disconnect();
    };
  }, []);

  useEffect(() => {
    onPreviewReady?.();
  }, [onPreviewReady]);

  // Build elements from props
  const elements = React.useMemo(() => {
    if (propElements) return propElements;
    if (textPositions) {
      return migrateLegacyPositions(
        textPositions,
        textConfig,
        fontSizes,
        fields
      );
    }
    return getDefaultElements();
  }, [propElements, textPositions, textConfig, fontSizes, fields]);

  const participantDisplayName = getCertificateParticipantDisplayName(sampleData, fields);
  const metaRows = getCertificateMetaRows({ fields, sampleData, workloadHours });
  const goldAccent = "#b8860b";
  const isProducer = mode === "producer";

  const getElement = useCallback(
    (id: CertificateElementId) => elements.find((e) => e.id === id),
    [elements]
  );

  const isEnabled = useCallback(
    (id: CertificateElementId) => getElement(id)?.enabled ?? false,
    [getElement]
  );

  const getPos = useCallback(
    (id: CertificateElementId): CertificateElementPosition =>
      getElement(id)?.position ?? { x: 50, y: 50 },
    [getElement]
  );

  const getStyle = useCallback(
    (id: CertificateElementId) => getElement(id)?.style ?? {},
    [getElement]
  );

  const getWidth = useCallback(
    (id: CertificateElementId) => getElement(id)?.width,
    [getElement]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, id: CertificateElementId) => {
      if (!isProducer || !onElementMove) return;
      e.preventDefault();
      e.stopPropagation();
      onSelectElement?.(id);
      const pos = getPos(id);
      setDragState({
        id,
        startX: e.clientX,
        startY: e.clientY,
        startPos: { ...pos },
      });
    },
    [isProducer, onElementMove, onSelectElement, getPos]
  );

  useEffect(() => {
    if (!dragState) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragState || !innerRef.current || !onElementMove) return;
      const deltaX = (e.clientX - dragState.startX) / scale;
      const deltaY = (e.clientY - dragState.startY) / scale;
      const newX = dragState.startPos.x + (deltaX / CERTIFICATE_WIDTH) * 100;
      const newY = dragState.startPos.y + (deltaY / CERTIFICATE_HEIGHT) * 100;
      onElementMove(dragState.id, {
        x: Math.max(0, Math.min(100, newX)),
        y: Math.max(0, Math.min(100, newY)),
      });
    };

    const handleMouseUp = () => {
      setDragState(null);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragState, onElementMove, scale]);

  const renderElementWrapper = (
    id: CertificateElementId,
    children: React.ReactNode,
    extraAlign?: "left" | "center" | "right"
  ) => {
    const pos = getPos(id);
    const isSelected = selectedElementId === id;
    const isDragging = dragState?.id === id;
    const elWidth = getWidth(id);

    const alignSelf = extraAlign === "left" ? "flex-start" : extraAlign === "right" ? "flex-end" : "center";

    return (
      <div
        key={id}
        className={cn(
          "absolute flex flex-col",
          isProducer && onElementMove && "cursor-move",
          isSelected && isProducer && "ring-2 ring-orange-500 ring-offset-1 ring-offset-transparent",
          isDragging && "z-50",
        )}
        style={{
          left: `${pos.x}%`,
          top: `${pos.y}%`,
          transform: "translate(-50%, -50%)",
          alignItems: alignSelf,
          zIndex: isDragging ? 50 : undefined,
          width: elWidth !== undefined ? `${elWidth}px` : "max-content",
          maxWidth: `${CERTIFICATE_WIDTH * 0.9}px`,
          minWidth: "1px",
          minHeight: "1px",
        }}
        onMouseDown={(e) => handleMouseDown(e, id)}
      >
        {elWidth !== undefined ? (
          <div className="w-full" style={{ minWidth: "1px", minHeight: "1px" }}>{children}</div>
        ) : (
          children
        )}
        {isProducer && isSelected && (
          <div className="mt-1 whitespace-nowrap">
            <span className="text-[9px] font-mono bg-orange-500 text-black px-1.5 py-0.5 rounded">
              {Math.round(pos.x)}%, {Math.round(pos.y)}%
            </span>
          </div>
        )}
      </div>
    );
  };

  const innerStyle: React.CSSProperties = {
    width: CERTIFICATE_WIDTH,
    height: CERTIFICATE_HEIGHT,
    position: "relative",
    overflow: "hidden",
    transform: `scale(${scale})`,
    transformOrigin: "top left",
    background: backgroundUrl ? undefined : "linear-gradient(145deg, #fefcf3 0%, #f8f4e8 40%, #f0ead6 100%)",
    color: textColor,
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  };

  return (
    <div
      ref={wrapperRef}
      className={cn("certificate-preview-wrapper flex items-center justify-center", className)}
      style={{ 
        width: "100%", 
        minHeight: Math.round(CERTIFICATE_HEIGHT * scale),
        overflow: "hidden" 
      }}
      onClick={() => onSelectElement?.(null)}
    >
      <div
        className="relative overflow-hidden shadow-xl select-none"
        style={{
          width: Math.round(CERTIFICATE_WIDTH * scale),
          height: Math.round(CERTIFICATE_HEIGHT * scale),
        }}
      >
        <div
          ref={innerRef}
          data-testid="certificate-preview"
          className="absolute top-0 left-0"
          style={innerStyle}
        >
          {/* Background Image */}
          {backgroundUrl && (
            <img
              src={backgroundUrl}
              alt=""
              className="absolute inset-0 h-full w-full object-cover"
              style={{ zIndex: 0 }}
              crossOrigin="anonymous"
            />
          )}

        {/* Content layer */}
        <div className="absolute inset-0" style={{ zIndex: 10 }}>
          {/* TITLE */}
          {isEnabled("title") && textConfig.title &&
            renderElementWrapper(
              "title",
              <h1
                style={{
                  fontSize: getFontSizePx(getStyle("title").fontSize),
                  fontWeight: getFontWeightCss(getStyle("title").fontWeight || "semibold"),
                  textTransform: getStyle("title").uppercase ? "uppercase" : undefined,
                  letterSpacing: getStyle("title").letterSpacing,
                  opacity: getStyle("title").color ? undefined : getStyle("title").opacity,
                  textAlign: "center",
                  color: getStyle("title").color || textColor,
                  lineHeight: 1.3,
                  width: "100%",
                }}
              >
                {textConfig.title}
              </h1>
            )}

          {/* TITLE DIVIDER */}
          {isEnabled("titleDivider") && textConfig.title &&
            renderElementWrapper(
              "titleDivider",
              <div className="flex items-center justify-center gap-2">
                <div className="h-px w-8" style={{ background: `${goldAccent}50` }} />
                <div className="h-1.5 w-1.5 rotate-45" style={{ background: `${goldAccent}60` }} />
                <div className="h-px w-8" style={{ background: `${goldAccent}50` }} />
              </div>
            )}

          {/* INTRO TEXT */}
          {isEnabled("introText") && textConfig.introText &&
            renderElementWrapper(
              "introText",
              <p
                style={{
                  fontSize: getFontSizePx(getStyle("introText").fontSize),
                  fontWeight: 400,
                  textTransform: getStyle("introText").uppercase ? "uppercase" : undefined,
                  letterSpacing: getStyle("introText").letterSpacing,
                  opacity: getStyle("introText").color ? undefined : getStyle("introText").opacity,
                  color: getStyle("introText").color || `${textColor}99`,
                  textAlign: "center",
                  width: "100%",
                }}
              >
                {textConfig.introText}
              </p>
            )}

          {/* PARTICIPANT NAME */}
          {isEnabled("participantName") && participantDisplayName &&
            renderElementWrapper(
              "participantName",
              <h2
                style={{
                  fontSize: getFontSizePx(getStyle("participantName").fontSize || fontSizes.name),
                  fontWeight: getFontWeightCss(getStyle("participantName").fontWeight || "bold"),
                  color: getStyle("participantName").color || textColor,
                  textShadow: "0 1px 2px rgba(0,0,0,0.04)",
                  lineHeight: 1.2,
                  textAlign: "center",
                  width: "100%",
                }}
              >
                {participantDisplayName}
              </h2>
            )}

          {/* NAME UNDERLINE */}
          {isEnabled("nameUnderline") && participantDisplayName &&
            renderElementWrapper(
              "nameUnderline",
              <div
                style={{
                  height: 1,
                  width: Math.min(220, CERTIFICATE_WIDTH * 0.4),
                  background: `linear-gradient(90deg, transparent 0%, ${goldAccent}50 50%, transparent 100%)`,
                }}
              />
            )}

          {/* CPF */}
          {isEnabled("cpf") && fields.showCPF && sampleData.participantCPF &&
            renderElementWrapper(
              "cpf",
              <p
                style={{
                  fontSize: getFontSizePx(getStyle("cpf").fontSize),
                  fontWeight: 500,
                  color: getStyle("cpf").color || `${textColor}80`,
                  letterSpacing: getStyle("cpf").letterSpacing,
                  opacity: getStyle("cpf").color ? undefined : getStyle("cpf").opacity,
                }}
              >
                CPF: {formatCpf(sampleData.participantCPF, !!fields.maskCPF)}
              </p>
            )}

          {/* PARTICIPATION TEXT */}
          {isEnabled("participationText") && textConfig.participationText &&
            renderElementWrapper(
              "participationText",
              <p
                style={{
                  fontSize: getFontSizePx(getStyle("participationText").fontSize),
                  fontWeight: 400,
                  color: getStyle("participationText").color || `${textColor}90`,
                  opacity: getStyle("participationText").color ? undefined : getStyle("participationText").opacity,
                  textAlign: "center",
                  width: "100%",
                }}
              >
                {textConfig.participationText}
              </p>
            )}

          {/* EVENT NAME */}
          {isEnabled("eventName") && fields.showEventName && sampleData.eventName &&
            renderElementWrapper(
              "eventName",
              <h3
                style={{
                  fontSize: getFontSizePx(getStyle("eventName").fontSize || fontSizes.event),
                  fontWeight: getFontWeightCss(getStyle("eventName").fontWeight || "semibold"),
                  color: getStyle("eventName").color || textColor,
                  lineHeight: 1.2,
                  textAlign: "center",
                  width: "100%",
                }}
              >
                {sampleData.eventName}
              </h3>
            )}

          {/* METADATA ROW */}
          {isEnabled("metadataRow") && metaRows.length > 0 &&
            renderElementWrapper(
              "metadataRow",
              <div className="flex flex-wrap justify-center items-center gap-x-1">
                {metaRows.map((row, index) => (
                  <React.Fragment key={`${row.label}-${index}`}>
                    {index > 0 && (
                      <div className="h-6 w-px mx-3" style={{ background: `${goldAccent}30` }} />
                    )}
                    <div className="flex flex-col items-center text-center px-2 py-1">
                      <span
                        className="font-semibold mb-0.5"
                        style={{
                          fontSize: 8,
                          textTransform: "uppercase",
                          letterSpacing: "0.22em",
                          color: `${goldAccent}cc`,
                        }}
                      >
                        {row.label}
                      </span>
                      <span className="font-semibold" style={{ fontSize: 12, color: getStyle("metadataRow").color || textColor }}>
                        {row.value}
                      </span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}

          {/* CONCLUSION TEXT */}
          {isEnabled("conclusionText") && textConfig.conclusionText &&
            renderElementWrapper(
              "conclusionText",
              <p
                style={{
                  fontSize: getFontSizePx(getStyle("conclusionText").fontSize),
                  fontWeight: 400,
                  color: getStyle("conclusionText").color || `${textColor}70`,
                  opacity: getStyle("conclusionText").color ? undefined : getStyle("conclusionText").opacity,
                  lineHeight: 1.5,
                  textAlign: "center",
                  width: "100%",
                }}
              >
                {textConfig.conclusionText}
              </p>
            )}

          {/* SIGNATURES */}
          {isEnabled("signatures") && fields.showSigners !== false &&
            renderElementWrapper(
              "signatures",
              <div className="flex gap-8 items-end">
                {signers.length > 0 ? (
                  signers.map((signer, index) => (
                    <div key={`${signer.name}-${index}`} className="flex flex-col items-center gap-0.5">
                      <div className="flex h-8 items-end justify-center mb-1">
                        {signer.signatureUrl ? (
                          <img
                            src={signer.signatureUrl}
                            alt=""
                            className="max-h-7 w-auto object-contain"
                            style={{
                              filter: textColor === "#ffffff" || textColor === "#fff" ? "invert(1)" : "none",
                            }}
                          />
                        ) : (
                          <div className="h-px w-28" style={{ background: `${textColor}50` }} />
                        )}
                      </div>
                      <p className="font-semibold leading-tight" style={{ fontSize: 10, color: textColor }}>
                        {signer.name}
                      </p>
                      <p
                        className="font-medium"
                        style={{
                          fontSize: 8,
                          textTransform: "uppercase",
                          letterSpacing: "0.15em",
                          color: `${textColor}70`,
                        }}
                      >
                        {signer.role}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center gap-0.5">
                    <div className="h-px w-32 mb-1.5 mt-6" style={{ background: `${textColor}40` }} />
                    <p className="font-semibold" style={{ fontSize: 10, color: textColor }}>
                      {t("preview.defaultSignerLabel")}
                    </p>
                  </div>
                )}
              </div>,
              "left"
            )}

          {/* VALIDATION LABEL */}
          {isEnabled("validationLabel") && fields.showQRCode !== false &&
            renderElementWrapper(
              "validationLabel",
              <p
                className="uppercase font-semibold"
                style={{
                  fontSize: getFontSizePx(getStyle("validationLabel").fontSize),
                  color: getStyle("validationLabel").color || `${goldAccent}99`,
                  letterSpacing: getStyle("validationLabel").letterSpacing,
                  opacity: getStyle("validationLabel").color ? undefined : getStyle("validationLabel").opacity,
                  textAlign: "center",
                  width: "100%",
                }}
              >
                {t("preview.digitalValidation")}
              </p>,
              "right"
            )}

          {/* VALIDATION URL */}
          {isEnabled("validationUrl") && fields.showQRCode !== false &&
            renderElementWrapper(
              "validationUrl",
              <p
                className="font-medium"
                style={{
                  fontSize: getFontSizePx(getStyle("validationUrl").fontSize),
                  color: getStyle("validationUrl").color || `${textColor}80`,
                  opacity: getStyle("validationUrl").color ? undefined : getStyle("validationUrl").opacity,
                  textAlign: "center",
                  width: "100%",
                }}
              >
                tickethall.com.br/verificar-certificado
              </p>,
              "right"
            )}

          {/* CERTIFICATE CODE */}
          {isEnabled("certificateCode") && sampleData.certificateCode &&
            renderElementWrapper(
              "certificateCode",
              <p
                className="font-mono tracking-wider"
                style={{
                  fontSize: getFontSizePx(getStyle("certificateCode").fontSize),
                  color: getStyle("certificateCode").color || `${textColor}55`,
                  opacity: getStyle("certificateCode").color ? undefined : getStyle("certificateCode").opacity,
                  textAlign: "center",
                  width: "100%",
                }}
              >
                {sampleData.certificateCode}
              </p>,
              "right"
            )}
        </div>
      </div>
    </div>
  </div>
  );
});
